import type { ProcessedImage } from '@/lib/types';

export type ImageIdentityState = {
  images?: ProcessedImage[];
  primaryImageId?: string | null;
  mainImageUrl?: string;
  mainImagePath?: string;
};

export function findImageById(images: ProcessedImage[] | undefined, imageId: string | null | undefined): ProcessedImage | undefined {
  if (!imageId) return undefined;
  return images?.find(image => image.id === imageId);
}

export function resolvePrimaryImage(state: ImageIdentityState): ProcessedImage | undefined {
  const found = findImageById(state.images, state.primaryImageId);
  if (found) return found;

  if (state.images && state.images.length > 0) {
    return state.images[0];
  }

  if (state.mainImageUrl || state.mainImagePath) {
    const ref = { url: state.mainImageUrl || '', path: state.mainImagePath || '' };
    return {
      id: state.primaryImageId || 'primary',
      original: ref,
      profile: ref,
      miniature: ref,
    };
  }

  return undefined;
}

export function primaryImageFields(images: ProcessedImage[], primaryImageId: string | null) {
  const image = findImageById(images, primaryImageId);
  return {
    primaryImageId,
    mainImageUrl: image?.profile?.url ?? image?.original?.url ?? '',
    mainImagePath: image?.profile?.path ?? image?.original?.path ?? '',
  };
}

export function getPrimaryAfterDeletion(images: ProcessedImage[], primaryImageId: string | null | undefined, deletedIndex: number): string | null {
  if (images[deletedIndex]?.id !== primaryImageId) return primaryImageId ?? null;
  const remaining = images.filter((_, index) => index !== deletedIndex);
  return remaining[deletedIndex]?.id ?? remaining[remaining.length - 1]?.id ?? null;
}

export function normalizeImageWrite<T extends Record<string, unknown>>(value: T): T {
  if (!Array.isArray(value.images)) return value;
  if (!('primaryImageId' in value)) return value;
  const images = value.images as ProcessedImage[];
  const primaryImageId = typeof value.primaryImageId === 'string' ? value.primaryImageId : null;
  const normalized = { ...value, ...primaryImageFields(images, primaryImageId) };
  return normalized;
}
